<template>
  <FilterOverview
    :title="'KPI Historie'"
    :isInitiallyLoading="false"
    :showFilterInitially="false"
    :showFilterButton="false"
  >
    <template #toolbar-left>
      <button
        type="button"
        class="icon-button filter-button d-flex justify-content-center p-0 mr-4"
        :class="{ 'filter-button--aktiv': isAnyKpiFilterApplied }"
        v-b-tooltip.hover.bottom
        title="Filter anpassen"
        @click="showFilter = !showFilter"
      >
        <span class="d-flex align-items-center"><Funnel :size="20" /></span>
      </button>
      <b-button-group size="sm" style="width: 300px">
        <b-button
          v-for="kpiSite in kpiSites"
          :disabled="isBusy"
          :key="kpiSite"
          :id="kpiSite"
          :variant="kpiSite === 'Historie' ? 'primary' : ''"
          class="mr-1"
          style="width: 33%"
          :to="'/reisetermine/fvc/kpi-' + kpiSite.toLowerCase()"
        >
          {{ kpiSite }}
        </b-button>
      </b-button-group>
      <template v-if="isAnyKpiFilterApplied">
        <div class="subheader-separator subheader-separator-ver mt-4 mb-2 mx-4 bg-gray-300"></div>
        <div
          class="icon-button d-flex align-items-center justify-content-center text-danger"
          @click="onResetFilter"
          v-b-tooltip.hover.bottom
          title="Filter zurücksetzen"
        >
          <FunnelX :size="20" />
        </div>
      </template>
    </template>

    <template #table="{ tableHeight, heightComponentInlineStyle }">
      <FilterDrawer :show="showFilter" @update:show="showFilter = $event">
        <KPIFilter :isHistoryPage="true" @apply="showFilter = false" @cancel="showFilter = false" />
      </FilterDrawer>
      <KpiLoadingOverlay :show="isBusy" :height="tableHeight + 87">
        <div class="d-flex mb-5" style="gap: 10px">
          <StackedLineChart
            style="width: 33%; height: 400px; flex-grow: 1"
            :data="isBusy ? [] : getHistoricGesamtverteilungStatusData"
            title="Verteilung Status"
            :fillArray="['#ebcc87', '#c16068', '#DAA17B', '#a2bf8a']"
            :stacked="false"
          />
          <StackedLineChart
            style="max-width: 33%; height: 400px; flex-grow: 1"
            :data="isBusy ? [] : getHistoricBearbeitungsData"
            title="Bearbeitete Daten"
            :fillArray="['#a2bf8a', '#c16068']"
            :stacked="true"
          />
          <StackedLineChart
            style="max-width: 33%; height: 400px; flex-grow: 1"
            :data="isBusy ? [] : getHistoricVerfuegbarkeitsData"
            title="Verteilung Bearbeitungsstatus"
            :fillArray="['#a2bf8a', '#c16068', '#1D4E89', '#D3D5D7']"
            :stacked="true"
          />
        </div>
        <div class="d-flex mb-5" style="gap: 10px">
          <StackedColumnChart
            style="width: 50%; height: 400px; flex-grow: 1"
            :data="isBusy ? [] : getHistoryNichtVerfuegbarAkzeptanzgrund"
            title="'Nicht verfügbar' Akzeptanzgründe"
            :stacked="true"
            :labelOnlyPercent="true"
          />
          <StackedColumnChart
            style="width: 50%; height: 400px; flex-grow: 1"
            :data="isBusy ? [] : getHistoryAufAnfrageAkzeptanzgrund"
            title="'Auf Anfrage' Akzeptanzgründe"
            :stacked="true"
            :labelOnlyPercent="true"
          />
        </div>
        <div class="d-flex mb-5" style="gap: 10px">
          <StackedColumnChart
            style="width: 50%; height: 400px; flex-grow: 1"
            :data="isBusy ? [] : getHistoryAufpreisGt200Akzeptanzgrund"
            title="'Aufpreis >200' Akzeptanzgründe"
            :stacked="true"
            :labelOnlyPercent="true"
          />
          <StackedColumnChart
            style="width: 50%; height: 400px; flex-grow: 1"
            :data="isBusy ? [] : getHistoricUrsachenData"
            title="Verfügbarkeit geschaffen: Ursachen"
            :stacked="true"
            :labelOnlyPercent="true"
          ></StackedColumnChart>
        </div>
      </KpiLoadingOverlay>
    </template>
  </FilterOverview>
</template>
<script>
import FilterOverview from '@/components/common/filter-overview.vue';
import FilterDrawer from '@/components/common/filter-drawer.vue';
import KpiLoadingOverlay from '@/components/flugverfuegbarkeit/kpi-loading-overlay.vue';
import { Funnel, FunnelX } from 'lucide-vue';
import { mapGetters, mapState } from 'vuex';
import StackedLineChart from '@/components/flugverfuegbarkeit/stacked-line-chart.vue';
import StackedColumnChart from '@/components/flugverfuegbarkeit/stacked-column-chart.vue';
import { GET_HISTORIC_DATA, RESET_ALL_FILTERS } from '@/core/flugverfuegbarkeit/stores/kpi-fvc.module';
import KPIFilter from '@/components/flugverfuegbarkeit/kpi-filter.vue';
import VerfuegbarkeitGeschaffenChart from '@/components/flugverfuegbarkeit/verfuegbarkeit-geschaffen-chart.vue';

export default {
  components: {
    KpiLoadingOverlay,
    FilterOverview,
    FilterDrawer,
    StackedLineChart,
    StackedColumnChart,
    VerfuegbarkeitGeschaffenChart,
    KPIFilter,
    Funnel,
    FunnelX,
  },
  data() {
    return {
      kpiSites: ['Dashboard', 'Leading', 'Historie'],
      showFilter: false,
    };
  },
  computed: {
    ...mapGetters([
      'isAnyKpiFilterApplied',
      'getHistoricGesamtverteilungStatusData',
      'getHistoryNichtVerfuegbarAkzeptanzgrund',
      'getHistoryAufAnfrageAkzeptanzgrund',
      'getHistoryAufpreisGt200Akzeptanzgrund',
      'getHistoricBearbeitungsData',
      'getHistoricVerfuegbarkeitsData',
      'getHistoricUrsachenData',
    ]),
    ...mapState({
      data: state => state.kpiFvc.data.historicData,
      isBusy: state => state.kpiFvc.isBusy,
    }),
  },
  created() {
    if (this.data.length === 0) this.$store.dispatch(GET_HISTORIC_DATA);
  },
  // Filter gelten nur solange die Seite offen ist, beim Verlassen werden sie geleert
  destroyed() {
    this.$store.commit(RESET_ALL_FILTERS);
  },
  methods: {
    onResetFilter() {
      this.$store.commit(RESET_ALL_FILTERS);
      this.showFilter = false;
    },
  },
};
</script>
<style></style>
